/**
 * InspectionCreatePage - Thin wrapper page for creating a new inspection.
 *
 * Delegates all form logic to the shared JobForm organism configured with
 * formType="inspection". The page provides the layout chrome (back link,
 * title) and navigation on successful creation.
 *
 * Two entry modes:
 *   1. Plain create  -- /inspections/create
 *      Empty form, defaults to today, current user as inspector.
 *   2. Duplicate     -- /inspections/create?duplicateFrom=:id
 *      Loads the source inspection (and its checklist template), strips
 *      visit-specific fields (date/times, status, photos, signatures,
 *      approvals), then prefills the form so the user can review/edit
 *      before saving as a NEW inspection (POST, not PATCH).
 *
 * On submit success, navigates to the newly created inspection's detail page.
 *
 * IMPORTANT: Inspections are SEPARATE from work orders -- never mixed.
 *
 * @route /inspections/create
 * @route /inspections/create?duplicateFrom=:id
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { JobForm } from '../../components/organisms/JobForm';
import { inspectionService } from '../../services/inspectionService';
import { camelizeKeys } from '../../services/api';
import { checklistInstanceService } from '../../services/checklistInstanceService';
import { BackLink, Alert } from '../../components/atoms';
import { PageContainer } from '../../components/templates/PageContainer';

/* ============================================
 * DUPLICATE PREFILL HELPER
 * ============================================
 * Build a JobForm initialData object from a source inspection. Carries
 * over identity, contact, settings, estimate, and notes — but
 * intentionally OMITS the visit-specific fields the user said should
 * reset on every duplicate:
 *   - inspection_date, start_time, end_time, completion_date
 *   - status (server defaults to 'pending')
 *   - photos, signature_data
 *   - estimate_approved_at / estimate_approved_by_*
 *
 * `inspector_id` is also omitted so the backend store() falls back to
 * the authenticated user (matches the standard create flow). */
const buildDuplicateInitialData = (source: Record<string, any>): Record<string, any> => {
	const next: Record<string, any> = { ...source };

	/* Drop the source identity so JobForm doesn't think this is an edit. */
	delete next.id;
	delete next.created_at;
	delete next.createdAt;
	delete next.updated_at;
	delete next.updatedAt;
	delete next.deleted_at;
	delete next.deletedAt;

	/* Reset visit timing fields. */
	delete next.inspection_date;
	delete next.inspectionDate;
	delete next.start_time;
	delete next.startTime;
	delete next.end_time;
	delete next.endTime;
	delete next.completion_date;
	delete next.completionDate;
	delete next.completed_date;
	delete next.completedDate;

	/* Reset lifecycle / role-of-record fields so the new inspection starts
	   fresh under the duplicating user. */
	delete next.status;
	delete next.inspector_id;
	delete next.inspectorId;
	delete next.inspector;

	/* Reset capture artifacts — photos and signatures don't carry over. */
	delete next.photos;
	delete next.signature_data;
	delete next.signatureData;

	/* Reset estimate approval (the dollar values still carry). */
	delete next.estimate_approved_at;
	delete next.estimateApprovedAt;
	delete next.estimate_approved_by_user_id;
	delete next.estimateApprovedByUserId;
	delete next.estimate_approved_by_contact_id;
	delete next.estimateApprovedByContactId;
	delete next.estimate_approved_by_user;
	delete next.estimateApprovedByUser;
	delete next.estimate_approved_by_contact;
	delete next.estimateApprovedByContact;

	/* Drop the source checklist instance — JobForm receives the source
	   template id via initialChecklistTemplateId and creates a fresh
	   instance on submit. */
	delete next.checklist_instance;
	delete next.checklistInstance;

	return next;
};

/* ============================================
 * COMPONENT
 * ============================================ */

const InspectionCreatePage: React.FC = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const duplicateFromId = searchParams.get('duplicateFrom');
	const isDuplicating = Boolean(duplicateFromId);

	/* When duplicating, we have to fetch the source record before the
	   JobForm can mount with initialData; render a small loading state
	   in the meantime so the empty form doesn't flash first. */
	const [duplicateInitialData, setDuplicateInitialData] = useState<Record<string, any> | null>(null);
	const [duplicateChecklistTemplateId, setDuplicateChecklistTemplateId] = useState<string>('');
	const [duplicateLoading, setDuplicateLoading] = useState(isDuplicating);
	const [duplicateError, setDuplicateError] = useState<string | null>(null);

	useEffect(() => {
		if (!duplicateFromId) return;
		let cancelled = false;
		(async () => {
			setDuplicateLoading(true);
			setDuplicateError(null);
			try {
				/* Fetch the source inspection and its checklist instance in
				   parallel — the inspection record itself doesn't carry the
				   attached template id (it lives on the related instance). */
				const [inspRes, checklistRes] = await Promise.all([
					inspectionService.getById(duplicateFromId),
					checklistInstanceService.getForInspection(duplicateFromId).catch(() => null),
				]);
				if (cancelled) return;

				/* The backend returns snake_case under a `data` wrapper; the
				   JobForm prefill effect tolerates both shapes via its v()
				   helper, but every other consumer (InspectionDetailPage edit
				   modal) camelizes first, so we follow the same path here. */
				const rawSource = (inspRes.data as any)?.data ?? inspRes.data;
				if (!rawSource) {
					setDuplicateError('Could not load the source inspection.');
					return;
				}
				const camelized = camelizeKeys(rawSource) as Record<string, any>;
				setDuplicateInitialData(buildDuplicateInitialData(camelized));

				/* Pull the source template id off the (optional) checklist
				   instance. Missing/non-existent is fine — the dropdown
				   simply stays empty. */
				const instance = (checklistRes?.data as any)?.data;
				const sourceTemplateId = instance?.sourceChecklistId ? String(instance.sourceChecklistId) : '';
				setDuplicateChecklistTemplateId(sourceTemplateId);
			} catch (err) {
				if (!cancelled) {
					setDuplicateError(err instanceof Error ? err.message : 'Failed to load source inspection.');
				}
			} finally {
				if (!cancelled) setDuplicateLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [duplicateFromId]);

	return (
		<PageContainer block="inspection-create-page" dataRef="page-inspection-create-page">
			{/* --- Back navigation link --- */}
			<BackLink to="/inspections" label="Back to Inspections" />

			{/* --- Page header — title shifts based on whether the user is duplicating --- */}
			<h1 className="page-title text-24px font-bold font-heading text-brand-heading mb-8px">
				{isDuplicating ? 'Duplicate Inspection' : 'New Inspection'}
			</h1>
			{isDuplicating && (
				<p className="text-14px text-brand-text-muted mb-24px">
					Job details have been carried over from the source inspection. Confirm the date and assignments, then save to create a new inspection.
				</p>
			)}
			{!isDuplicating && <div className="mb-24px" />}

			{/* --- Source-load error banner --- */}
			{duplicateError && (
				<Alert variant="error" className="mb-16px">{duplicateError}</Alert>
			)}

			{/* --- JobForm — wait for the source data when duplicating --- */}
			{isDuplicating && duplicateLoading ? (
				<div className="text-14px text-brand-text-muted py-16px">Loading source inspection…</div>
			) : (
				<JobForm
					formType="inspection"
					onSuccess={(id) => navigate(`/inspections/${id}`)}
					initialData={duplicateInitialData ?? undefined}
					initialChecklistTemplateId={duplicateChecklistTemplateId || undefined}
				/>
			)}
		</PageContainer>
	);
};

export default InspectionCreatePage;
